import React, { useState, useEffect, useMemo } from 'react';
import '../styles/DeviceSettingsDialog.css';
import { getDeviceSettings, DeviceSettings } from '../utils/device';
import { collectDeviceProfile, DeviceProfile } from '../utils/deviceProfile';
import { competitionDb } from '../utils/competitionDb';
import config from '../config';

interface DeviceSettingsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  user: any;
  onLogout: () => void;
  daysRemaining?: number | null;
  syncFailCount?: number;
  onCompetitionSaved?: (pouleId: number, teamId?: number) => void;
}

const formatMobileNumber = (mobile?: string) => {
  if (!mobile) return '---';
  // Remove non-numeric characters except +
  let cleaned = mobile.replace(/[^\d+]/g, '');
  
  // Convert Dutch local format to international if needed
  if (cleaned.startsWith('06')) {
    cleaned = '+316' + cleaned.substring(2);
  } else if (cleaned.startsWith('316')) {
    cleaned = '+316' + cleaned.substring(3);
  }
  
  // Format as +31 6 XXX XX XXX
  if (cleaned.startsWith('+316') && cleaned.length === 12) {
    const part1 = cleaned.substring(4, 7);
    const part2 = cleaned.substring(7, 9);
    const part3 = cleaned.substring(9, 12);
    return `+31 6 ${part1} ${part2} ${part3}`;
  }
  
  return mobile;
};

const LOCAL_STORAGE_DEVICE_KEY = 'squash_device_settings';

const DeviceSettingsDialog: React.FC<DeviceSettingsDialogProps> = ({
  isOpen,
  onClose,
  user,
  onLogout,
  daysRemaining,
  syncFailCount = 0,
  onCompetitionSaved
}) => {
  const [profile, setProfile] = useState<DeviceProfile | null>(null);
  const [currentSettings, setCurrentSettings] = useState<DeviceSettings | null>(null);
  const [deviceId, setDeviceId] = useState<string>('');
  const [isSavingCompetition, setIsSavingCompetition] = useState(false);

  // Fallback weergave states (vanuit IndexedDB)
  const [pouleInfo, setPouleInfo] = useState<any>(null);
  const [userTeam, setUserTeam] = useState<any>(null);

  const [lastSync, setLastSync] = useState<string>('Nooit');
  const [lastCheck, setLastCheck] = useState<string>('Nooit');
  const [isDeleting, setIsDeleting] = useState(false);

  // ── Competitie-instellingen edit mode ──────────────────────────────────
  const [isEditingCompetition, setIsEditingCompetition] = useState(false);
  const [allPoules, setAllPoules] = useState<any[]>([]);
  const [availableTeams, setAvailableTeams] = useState<any[]>([]);
  const [isFetchingPoules, setIsFetchingPoules] = useState(false);
  const [isFetchingTeams, setIsFetchingTeams] = useState(false);

  // Formulierwaarden (cascading)
  const [formSeizoen, setFormSeizoen] = useState('');
  const [formGeslacht, setFormGeslacht] = useState('');
  const [formKlasse, setFormKlasse] = useState('');
  const [formAfdeling, setFormAfdeling] = useState('');
  const [formTeamId, setFormTeamId] = useState<string>('');
  const [formPouleId, setFormPouleId] = useState<number | undefined>(undefined);

  // ── Init effect ────────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      const settings = getDeviceSettings();
      setCurrentSettings(settings);
      setDeviceId(settings.deviceId);
      setProfile(collectDeviceProfile());

      const loadContext = async () => {
        try {
          const info = await competitionDb.getPouleInfo();
          const teams = await competitionDb.getTeams();
          const status = await competitionDb.getSyncStatus();

          setPouleInfo(info);
          if (status.lastSync) {
            setLastSync(new Date(status.lastSync).toLocaleString('nl-NL'));
          }
          if (status.lastCheck) {
            setLastCheck(new Date(status.lastCheck).toLocaleString('nl-NL'));
          }

          // Find user's team
          if (user?.playerId && teams.length > 0) {
            const players = await competitionDb.getPlayers();
            const playerEntry = players.find((p: any) => p.id === user.playerId);
            if (playerEntry) {
              const team = teams.find((t: any) => t.id === playerEntry.team_id);
              setUserTeam(team);
            }
          }
        } catch (e) {
          console.error('[SETTINGS] Failed to load competition context', e);
        }
      };
      loadContext();
    }
  }, [isOpen, user]);

  // ── Haal poules op als edit mode opent ────────────────────────────────
  useEffect(() => {
    if (isEditingCompetition && allPoules.length === 0) {
      setIsFetchingPoules(true);
      fetch(`${config.apiUrl}/api/v1/competition/poules`)
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            setAllPoules(data.poules);
          }
        })
        .catch(e => console.error('[SETTINGS] Fout bij ophalen poules', e))
        .finally(() => setIsFetchingPoules(false));
    }
  }, [isEditingCompetition, allPoules.length]);

  // ── Cascading dropdown opties (gefilterd client-side op DB-data) ───────
  const availableSeizoenen = useMemo(() => {
    return Array.from(new Set(allPoules.map((p: any) => p.competition_name).filter(Boolean))).sort().reverse();
  }, [allPoules]);

  const availableGeslachten = useMemo(() => {
    if (!formSeizoen) return [];
    return Array.from(new Set(
      allPoules
        .filter((p: any) => p.competition_name === formSeizoen)
        .map((p: any) => p.type)
        .filter(Boolean)
    )).sort() as string[];
  }, [allPoules, formSeizoen]);

  const availableKlassen = useMemo(() => {
    if (!formSeizoen || !formGeslacht) return [];
    return Array.from(new Set(
      allPoules
        .filter((p: any) => p.competition_name === formSeizoen && p.type === formGeslacht)
        .map((p: any) => p.klasse)
        .filter(Boolean)
    )).sort() as string[];
  }, [allPoules, formSeizoen, formGeslacht]);

  const availableAfdelingen = useMemo(() => {
    if (!formSeizoen || !formGeslacht || !formKlasse) return [];
    return Array.from(new Set(
      allPoules
        .filter((p: any) => p.competition_name === formSeizoen && p.type === formGeslacht && p.klasse === formKlasse)
        .map((p: any) => p.afdeling)
        .filter(Boolean)
    )).sort() as string[];
  }, [allPoules, formSeizoen, formGeslacht, formKlasse]);

  // ── Effect: afdeling geselecteerd → zoek pouleId en haal teams op ─────
  useEffect(() => {
    if (formSeizoen && formGeslacht && formKlasse && formAfdeling) {
      const matchedPoule = allPoules.find((p: any) =>
        p.competition_name === formSeizoen &&
        p.type === formGeslacht &&
        p.klasse === formKlasse &&
        p.afdeling === formAfdeling
      );
      if (matchedPoule) {
        setFormPouleId(matchedPoule.id);
        setIsFetchingTeams(true);
        fetch(`${config.apiUrl}/api/v1/competition/sync/${matchedPoule.id}`)
          .then(res => res.json())
          .then(data => {
            if (data.success && data.data && data.data.teams) {
              setAvailableTeams(data.data.teams);
            } else {
              setAvailableTeams([]);
            }
          })
          .catch(e => {
            console.error('[SETTINGS] Fout bij ophalen teams', e);
            setAvailableTeams([]);
          })
          .finally(() => setIsFetchingTeams(false));
      } else {
        setFormPouleId(undefined);
        setAvailableTeams([]);
      }
    } else {
      setFormPouleId(undefined);
      setAvailableTeams([]);
    }
  }, [formSeizoen, formGeslacht, formKlasse, formAfdeling, allPoules]);

  // ── Edit-knop handler ─────────────────────────────────────────────────
  const handleEditClick = () => {
    if (!isEditingCompetition && currentSettings) {
      // Initialiseer formulier met huidige opgeslagen waarden
      setFormSeizoen(currentSettings.seizoen || '');
      setFormGeslacht(currentSettings.geslacht || '');
      setFormKlasse(currentSettings.klasse || '');
      setFormAfdeling(currentSettings.afdeling || '');
      setFormTeamId(currentSettings.teamId !== undefined ? String(currentSettings.teamId) : '');
    }
    setIsEditingCompetition(prev => !prev);
  };

  // ── Opslaan handler ───────────────────────────────────────────────────
  const handleSaveCompetition = async () => {
    setIsSavingCompetition(true);
    try {
      const selectedTeam = availableTeams.find((t: any) => t.id === Number(formTeamId));
      const parsedTeamId = formTeamId ? Number(formTeamId) : undefined;
      const savedSettings: DeviceSettings = {
        ...(currentSettings!),
        seizoen: formSeizoen || undefined,
        geslacht: formGeslacht || undefined,
        klasse: formKlasse || undefined,
        afdeling: formAfdeling || undefined,
        pouleId: formPouleId,
        teamId: parsedTeamId,
        teamName: selectedTeam ? selectedTeam.name : undefined,
      };

      localStorage.setItem(LOCAL_STORAGE_DEVICE_KEY, JSON.stringify(savedSettings));
      setCurrentSettings(savedSettings);

      // Als een poule is geselecteerd, synchroniseer direct alle data naar IndexedDB
      if (formPouleId) {
        // Stel direct het geselecteerde team in als filter voor deze poule
        if (parsedTeamId) {
          localStorage.setItem(`competition_teams_${formPouleId}`, JSON.stringify([parsedTeamId]));
        }

        try {
          const syncRes = await fetch(`${config.apiUrl}/api/v1/competition/sync/${formPouleId}`);
          const syncData = await syncRes.json();
          if (syncData.success && syncData.data) {
            await competitionDb.saveSyncData(syncData.data);
            setPouleInfo(syncData.data.poule);
            if (selectedTeam) {
              setUserTeam(selectedTeam);
            }
            setLastSync(new Date().toLocaleString('nl-NL'));
            console.log(`[SETTINGS] Succesvol data gesynchroniseerd voor poule ${formPouleId}`);
          }
        } catch (syncErr) {
          console.error('[SETTINGS] Fout bij synchroniseren na opslaan competitie:', syncErr);
        }

        if (onCompetitionSaved) {
          onCompetitionSaved(formPouleId, parsedTeamId);
        }
      }

      setIsEditingCompetition(false);
    } catch (e) {
      console.error('[SETTINGS] Fout bij opslaan competitie-instellingen:', e);
    } finally {
      setIsSavingCompetition(false);
    }
  };

  // ── Profiel verwijderen ───────────────────────────────────────────────
  const handleDeleteProfile = async () => {
    if (!window.confirm('Weet je zeker dat je jouw profiel op dit apparaat wilt verwijderen? Alle lokale data wordt gewist en passkeys worden ongeldig gemaakt.')) {
      return;
    }

    setIsDeleting(true);
    try {
      // 1. Backend reset
      const token = localStorage.getItem('token');
      if (token) {
        await fetch(`${config.apiUrl}/api/v1/auth/full-reset`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            deviceId,
            deviceProfile: profile
          })
        });
      }

      // 2. Clear LocalStorage
      localStorage.clear();

      // 3. Clear Cookies
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf('=');
        const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;
        document.cookie = name + '=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/';
      }

      // 4. Clear Cache Storage (Service Worker)
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }

      // 5. Reload to login
      window.location.reload();
    } catch (err) {
      console.error('Fout bij verwijderen profiel:', err);
      alert('Er is een fout opgetreden bij het verwijderen van het profiel.');
      setIsDeleting(false);
    }
  };

  if (!isOpen) return null;

  // ── Weergave-waardes: LocalStorage heeft prioriteit boven user-object ──
  const displaySeizoen = currentSettings?.seizoen || user?.competition_name || pouleInfo?.competition_name || '---';
  const displaySoort = currentSettings?.geslacht || pouleInfo?.type || '---';
  const displayPoule = (currentSettings?.klasse && currentSettings?.afdeling)
    ? `${currentSettings.klasse} - ${currentSettings.afdeling}`
    : (user?.poule_name || pouleInfo?.name || '---');
  const displayTeam = currentSettings?.teamName || user?.team_name || userTeam?.name || '---';

  return (
    <div className="device-dialog-overlay">
      <div className="device-dialog dashboard-mode">
        <div className="device-header">
          <h2>Systeeminstellingen</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="device-content">
          <div className="settings-dashboard">

            {/* 1. Gebruikersprofiel */}
            <section className="dashboard-section">
              <h3>Gebruikersprofiel</h3>
              <div className="info-grid">
                <div className="info-row"><label>Naam:</label> <span>{user?.name || '---'}</span></div>
                <div className="info-row">
                  <label>Email:</label>
                  <span>
                    {user?.email || '---'}
                    {user?.email_verified && <span className="verify-tag" title="Geverifieerd"> ✅</span>}
                  </span>
                </div>
                <div className="info-row">
                  <label>Mobiel:</label>
                  <span>
                    {formatMobileNumber(user?.mobile_number || user?.mobile)}
                    {user?.mobile_verified && <span className="verify-tag" title="Geverifieerd via WhatsApp"> ✅</span>}
                  </span>
                </div>
              </div>
            </section>

            {/* 2. Competitie & Club */}
            <section className="dashboard-section">
              <div className="section-header-row">
                <h3>Competitie &amp; Club</h3>
                <button
                  className="section-gear-btn"
                  onClick={handleEditClick}
                  title="Competitie-instellingen bewerken"
                >
                  ⚙️
                </button>
              </div>

              {isEditingCompetition ? (
                <div className="competition-edit-form">
                  {isFetchingPoules && (
                    <div className="fetch-status">Poules laden...</div>
                  )}

                  {/* Dropdown 1: Seizoen */}
                  <div className="form-group">
                    <label>Seizoen</label>
                    <select
                      value={formSeizoen}
                      onChange={e => {
                        setFormSeizoen(e.target.value);
                        setFormGeslacht('');
                        setFormKlasse('');
                        setFormAfdeling('');
                        setFormTeamId('');
                      }}
                    >
                      <option value="">-- Selecteer Seizoen --</option>
                      {availableSeizoenen.map((s: any) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  {/* Dropdown 2: Soort (Geslacht) */}
                  <div className="form-group">
                    <label>Soort</label>
                    <select
                      value={formGeslacht}
                      onChange={e => {
                        setFormGeslacht(e.target.value);
                        setFormKlasse('');
                        setFormAfdeling('');
                        setFormTeamId('');
                      }}
                      disabled={!formSeizoen}
                    >
                      <option value="">-- Selecteer Soort --</option>
                      {availableGeslachten.map((g: string) => (
                        <option key={g} value={g}>{g}</option>
                      ))}
                    </select>
                  </div>

                  {/* Dropdown 3: Klasse */}
                  <div className="form-group">
                    <label>Klasse</label>
                    <select
                      value={formKlasse}
                      onChange={e => {
                        setFormKlasse(e.target.value);
                        setFormAfdeling('');
                        setFormTeamId('');
                      }}
                      disabled={!formGeslacht}
                    >
                      <option value="">-- Selecteer Klasse --</option>
                      {availableKlassen.map((k: string) => (
                        <option key={k} value={k}>{k}</option>
                      ))}
                    </select>
                  </div>

                  {/* Dropdown 4: Afdeling */}
                  <div className="form-group">
                    <label>Afdeling</label>
                    <select
                      value={formAfdeling}
                      onChange={e => {
                        setFormAfdeling(e.target.value);
                        setFormTeamId('');
                      }}
                      disabled={!formKlasse}
                    >
                      <option value="">-- Selecteer Afdeling --</option>
                      {availableAfdelingen.map((a: string) => (
                        <option key={a} value={a}>{a}</option>
                      ))}
                    </select>
                  </div>

                  {/* Dropdown 5: Team */}
                  <div className="form-group">
                    <label>Team</label>
                    {isFetchingTeams && (
                      <div className="fetch-status">Teams laden...</div>
                    )}
                    <select
                      value={formTeamId}
                      onChange={e => setFormTeamId(e.target.value)}
                      disabled={!formAfdeling || isFetchingTeams || availableTeams.length === 0}
                    >
                      <option value="">-- Selecteer Team --</option>
                      {availableTeams.map((t: any) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-actions">
                    <button
                      className="btn-cancel"
                      onClick={() => setIsEditingCompetition(false)}
                    >
                      Annuleren
                    </button>
                    <button
                      className="btn-save"
                      onClick={handleSaveCompetition}
                      disabled={!formTeamId || isSavingCompetition}
                    >
                      {isSavingCompetition ? 'Opslaan & Syncen...' : 'Opslaan'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="info-grid">
                  <div className="info-row"><label>Seizoen:</label> <span>{displaySeizoen}</span></div>
                  <div className="info-row"><label>Soort:</label> <span>{displaySoort}</span></div>
                  <div className="info-row"><label>Poule:</label> <span>{displayPoule}</span></div>
                  <div className="info-row"><label>Team:</label> <span>{displayTeam}</span></div>
                </div>
              )}
            </section>

            {/* 3. Licentie & Data Status */}
            <section className="dashboard-section">
              <h3>Licentie &amp; Data</h3>
              <div className="info-grid">
                <div className="info-row">
                  <label>Licentie status:</label>
                  <span className={daysRemaining !== null && daysRemaining !== undefined && daysRemaining < 7 ? 'warning' : ''}>
                    {daysRemaining !== null ? `${daysRemaining} dagen resterend` : 'Onbekend'}
                  </span>
                </div>
                <div className="info-row"><label>Laatste refresh:</label> <span>{lastSync}</span></div>
                <div className="info-row">
                  <label>Server check:</label>
                  <span>
                    {lastCheck}
                    {syncFailCount > 0 && (
                      <span className="fail-badge" style={{ color: '#FF4D4D', marginLeft: '6px', fontWeight: 'bold' }}>
                        ({syncFailCount})
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </section>

            {/* 4. Technische Details (Gedegradeerd naar onderen) */}
            <section className="dashboard-section technical-details">
              <details>
                <summary>Technische Details</summary>
                <div className="info-grid technical">
                  <div className="info-row"><label>Device ID:</label> <span>{deviceId}</span></div>
                  {profile && (
                    <>
                      <div className="info-row"><label>Browser:</label> <span>{profile.browser.name} {profile.browser.version}</span></div>
                      <div className="info-row"><label>Systeem:</label> <span>{profile.os.name} {profile.os.version}</span></div>
                      <div className="info-row"><label>Resolutie:</label> <span>{profile.screen.width}x{profile.screen.height}</span></div>
                      <div className="info-row"><label>Touch:</label> <span>{profile.capabilities.isTouch ? 'Ja' : 'Nee'}</span></div>
                    </>
                  )}
                  <div className="delete-profile-container">
                    <button
                      className="delete-profile-btn"
                      onClick={handleDeleteProfile}
                      disabled={isDeleting}
                    >
                      {isDeleting ? 'Verwijderen...' : '🗑️ Profiel verwijderen'}
                    </button>
                    <p className="delete-info">
                      Wist alle gegevens, cookies en passkeys van dit apparaat.
                    </p>
                  </div>
                </div>
              </details>
            </section>

            <div className="dashboard-footer">
              {/* Logout verwijderd op verzoek */}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceSettingsDialog;
